/* RESPONSIBLE TEAM: team-standalone */

import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import type RouterService from '@ember/routing/router-service';
import { hideIntercomWidgetWhenLoaded } from 'embercom/lib/intercom-widget-helper';
import { request, ResponseError } from 'embercom/lib/inbox/requests';
import Conversation, { type ConversationWireFormat } from 'embercom/objects/inbox/conversation';
import { COPILOT_EXTERNAL_TYPE_MAPPING } from 'embercom/components/standalone/adapters/copilot-adapter';
import { InboxCategory } from 'embercom/models/data/inbox/inbox-categories';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';
import type Transition from '@ember/routing/transition';
import type CopilotApi from 'embercom/services/copilot-api';

interface ConversationFrameParams {
  external_id: string;
  external_type: string;
}

export default class InboxWorkspaceConversationFrameRoute extends Route {
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare copilotApi: CopilotApi;

  queryParams = {
    external_id: { refreshModel: true },
    external_type: { refreshModel: true },
  };

  async beforeModel(transition: Transition) {
    hideIntercomWidgetWhenLoaded();

    if (!this.session.workspace.isFeatureEnabled('team-standalone-embedded-conversation-view')) {
      transition.abort();
      this.router.transitionTo('inbox.workspace.inbox.inbox', InboxCategory.Shared, InboxType.All);
    }

    // @ts-expect-error - For this experiment, we need to directly disable copilot and I want to do it here
    // rather than in the 'real' product code used elsewhere.
    this.copilotApi._isCopilotReadyToUse = false;
  }

  async model(params: ConversationFrameParams) {
    if (!params.external_id || !params.external_type) {
      throw new Error('Missing external_id or external_type parameters');
    }

    try {
      let externalType =
        COPILOT_EXTERNAL_TYPE_MAPPING[
          params.external_type as keyof typeof COPILOT_EXTERNAL_TYPE_MAPPING
        ];

      let url = `/ember/inbox/conversations/external_conversation?app_id=${this.session.workspace.id}&external_id=${encodeURIComponent(params.external_id)}&external_type=${encodeURIComponent(externalType)}`;

      let response = await request(url);
      let json = (await response.json()) as ConversationWireFormat;

      return Conversation.deserialize(json);
    } catch (error) {
      if (error instanceof ResponseError && error.response.status === 404) {
        // Return null for 404s so we can handle them in the template
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  }
}
